import React, { useCallback } from 'react';

import {
  Button,
  Checkbox,
  FormGroup,
  H1,
  Intent,
  NumericInput,
  Position,
} from '@blueprintjs/core';

import { ItemActions } from './styles';

import Unit from '../Unit';

import { WorkflowMeasurements } from '../../types';

interface MeasurementsProps {
  title?: boolean;
  measurements: WorkflowMeasurements;
  onMeasurementsUpdate: (measurements: WorkflowMeasurements) => void;
  onMeasurementsSave?: (measurements: WorkflowMeasurements) => void;
}

const Measurements = ({
  title,
  measurements,
  onMeasurementsUpdate,
  onMeasurementsSave,
}: MeasurementsProps) => {
  const updateField = useCallback(
    (pack: any) => {
      onMeasurementsUpdate({
        ...measurements,
        ...pack,
      });
    },
    [measurements, onMeasurementsUpdate],
  );

  const measurementsSave = useCallback(() => {
    if (onMeasurementsSave) {
      onMeasurementsSave(measurements);
    }
  }, [measurements, onMeasurementsSave]);

  const toggleIgnoreAll = useCallback(() => {
    onMeasurementsUpdate({
      ...measurements,
      ignore: {
        ignoreAll: !measurements.ignore.ignoreAll,
        ignoreBpm: !measurements.ignore.ignoreAll,
        ignorebloodPreassure: !measurements.ignore.ignoreAll,
        ignorebloodO2Saturation: !measurements.ignore.ignoreAll,
        ignoreRespiratoryRate: !measurements.ignore.ignoreAll,
        ignoreGlucose: !measurements.ignore.ignoreAll,
      },
    });
  }, [onMeasurementsUpdate, measurements]);

  const toggleIgnoreBPM = useCallback(() => {
    onMeasurementsUpdate({
      ...measurements,
      ignore: {
        ...measurements.ignore,
        ignoreAll: false,
        ignoreBpm: !measurements.ignore.ignoreBpm,
      },
    });
  }, [onMeasurementsUpdate, measurements]);

  const toggleIgnoreBloodPreassure = useCallback(() => {
    onMeasurementsUpdate({
      ...measurements,
      ignore: {
        ...measurements.ignore,
        ignoreAll: false,
        ignorebloodPreassure: !measurements.ignore.ignorebloodPreassure,
      },
    });
  }, [onMeasurementsUpdate, measurements]);

  const toggleIgnoreOximetry = useCallback(() => {
    onMeasurementsUpdate({
      ...measurements,
      ignore: {
        ...measurements.ignore,
        ignoreAll: false,
        ignorebloodO2Saturation: !measurements.ignore.ignorebloodO2Saturation,
      },
    });
  }, [onMeasurementsUpdate, measurements]);

  const toggleIgnoreRespiratory = useCallback(() => {
    onMeasurementsUpdate({
      ...measurements,
      ignore: {
        ...measurements.ignore,
        ignoreAll: false,
        ignoreRespiratoryRate: !measurements.ignore.ignoreRespiratoryRate,
      },
    });
  }, [measurements, onMeasurementsUpdate]);

  const toggleIgnoreGlucose = useCallback(() => {
    onMeasurementsUpdate({
      ...measurements,
      ignore: {
        ...measurements.ignore,
        ignoreAll: false,
        ignoreGlucose: !measurements.ignore.ignoreGlucose,
      },
    });
  }, [measurements, onMeasurementsUpdate]);

  return (
    <>
      {title && <H1 data-testid="measurements-title">Medidas</H1>}
      <Checkbox
        checked={measurements.ignore.ignoreAll}
        onChange={toggleIgnoreAll}
        name="ignore-all"
      >
        Ignorar todos
      </Checkbox>
      <FormGroup
        helperText={'Informe os batimentos cardíacos medido'}
        intent={Intent.PRIMARY}
        label={'Batimentos Cardíacos'}
        labelFor="bpm-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="bpm-input"
          data-testid="bpm-input"
          intent={Intent.PRIMARY}
          leftIcon="pulse"
          value={measurements.bpm}
          onValueChange={(value: number) => updateField({ bpm: value })}
          stepSize={1}
          max={200}
          rightElement={<Unit>bpm</Unit>}
          buttonPosition={Position.LEFT}
          disabled={measurements.ignore.ignoreBpm}
        />
        <Checkbox
          checked={measurements.ignore.ignoreBpm}
          onChange={toggleIgnoreBPM}
          name="ignore-bpm"
        >
          Ignorar batimentos cardíacos
        </Checkbox>
      </FormGroup>
      <FormGroup
        helperText={'Informe a pressão sanguínea medida (Pressão Sistólica)'}
        intent={Intent.PRIMARY}
        label={'Pressão Sistólica'}
        labelFor="preassure-sistole-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="preassure-sistole-input"
          data-testid="preassure-sistole-input"
          intent={Intent.PRIMARY}
          leftIcon="heart"
          value={measurements.bloodPreassure.systolic}
          onValueChange={(value: number) =>
            updateField({
              bloodPreassure: {
                systolic: value,
                diastolic: measurements.bloodPreassure.diastolic,
              },
            })
          }
          rightElement={<Unit>mmHg</Unit>}
          buttonPosition={Position.LEFT}
          max={300}
          disabled={measurements.ignore.ignorebloodPreassure}
        />
      </FormGroup>
      <FormGroup
        helperText={'Informe a pressão sanguínea medida (Pressão Diastólica)'}
        intent={Intent.PRIMARY}
        label={'Pressão Diastólica'}
        labelFor="preassure-diastole-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="preassure-diastole-input"
          data-testid="preassure-diastole-input"
          intent={Intent.PRIMARY}
          leftIcon="heart"
          value={measurements.bloodPreassure.diastolic}
          onValueChange={(value: number) =>
            updateField({
              bloodPreassure: {
                diastolic: value,
                systolic: measurements.bloodPreassure.systolic,
              },
            })
          }
          rightElement={<Unit>mmHg</Unit>}
          buttonPosition={Position.LEFT}
          max={300}
          disabled={measurements.ignore.ignorebloodPreassure}
        />
        <Checkbox
          checked={measurements.ignore.ignorebloodPreassure}
          onChange={toggleIgnoreBloodPreassure}
        >
          Ignorar pressão sanguínea
        </Checkbox>
      </FormGroup>
      <FormGroup
        helperText={'Informe saturação de O2 medida'}
        intent={Intent.PRIMARY}
        label={'Saturação'}
        labelFor="saturation-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="saturation-input"
          data-testid="saturation-input"
          intent={Intent.PRIMARY}
          leftIcon="waves"
          value={measurements.bloodO2Saturation}
          onValueChange={(value: number) =>
            updateField({ bloodO2Saturation: value })
          }
          rightElement={<Unit>%</Unit>}
          buttonPosition={Position.LEFT}
          min={0}
          max={100}
          disabled={measurements.ignore.ignorebloodO2Saturation}
        />
        <Checkbox
          checked={measurements.ignore.ignorebloodO2Saturation}
          onChange={toggleIgnoreOximetry}
        >
          Ignorar oximetria
        </Checkbox>
      </FormGroup>
      <FormGroup
        helperText={'Informe a frequência respiratória medida'}
        intent={Intent.PRIMARY}
        label={'Frequência Respiratória'}
        labelFor="respiratory-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="respiratory-input"
          data-testid="respiratory-input"
          intent={Intent.PRIMARY}
          leftIcon="wind"
          value={measurements.respiratoryRate}
          onValueChange={(value: number) =>
            updateField({ respiratoryRate: value })
          }
          rightElement={<Unit>irpm</Unit>}
          buttonPosition={Position.LEFT}
          disabled={measurements.ignore.ignoreRespiratoryRate}
        />
        <Checkbox
          checked={measurements.ignore.ignoreRespiratoryRate}
          onChange={toggleIgnoreRespiratory}
        >
          Ignorar frequência respiratória
        </Checkbox>
      </FormGroup>
      <FormGroup
        helperText={'Informe a glicose medida'}
        intent={Intent.PRIMARY}
        label={'Glicose'}
        labelFor="glucose-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          selectAllOnFocus
          fill
          id="glucose-input"
          data-testid="glucose-input"
          intent={Intent.PRIMARY}
          leftIcon="segmented-control"
          value={measurements.glucose}
          onValueChange={(value: number) => updateField({ glucose: value })}
          rightElement={<Unit>mg/dL</Unit>}
          buttonPosition={Position.LEFT}
          disabled={measurements.ignore.ignoreGlucose}
        />
        <Checkbox
          checked={measurements.ignore.ignoreGlucose}
          onChange={toggleIgnoreGlucose}
        >
          Ignorar glicose
        </Checkbox>
      </FormGroup>
      {onMeasurementsSave !== undefined && (
        <ItemActions>
          <Button
            intent={Intent.PRIMARY}
            onClick={measurementsSave}
            data-testid="save-measurements"
          >
            Salvar Medidas
          </Button>
        </ItemActions>
      )}
    </>
  );
};

export default Measurements;
